import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  basicDesignExpansionApi,
  type ExpandBasicDesignInput,
  type ExpandChunksInput,
} from '../lib/api/basic-design-expansion.js';

/** 展開履歴の query key。展開に成功したら同じキーを捨てて取り直す。 @graph-connects none */
export function basicDesignExpansionHistoryKey(pjKey: string): string[] {
  return ['basic-design', 'expansion-history', pjKey];
}

/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 基本設計展開の履歴を取得するフック
 * @graph-connects /basic-design/:pjKey/expansion-history [calls] 展開履歴取得API
 */
export function useBasicDesignExpansionHistory(pjKey: string) {
  return useQuery({
    queryKey: basicDesignExpansionHistoryKey(pjKey),
    queryFn: () => basicDesignExpansionApi.listHistory(pjKey),
    enabled: pjKey.length > 0,
  });
}

/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 基本設計シートを Backlog の子票へ展開するフック。成功したら履歴を取り直す
 * @graph-connects /basic-design/:pjKey/expand [calls] 基本設計展開API
 */
export function useExpandBasicDesign(pjKey: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: ExpandBasicDesignInput) => basicDesignExpansionApi.expand(pjKey, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: basicDesignExpansionHistoryKey(pjKey) });
    },
  });
}

/** 展開済みチャンク台帳の query key。 @graph-connects none */
export function basicDesignChunksKey(pjKey: string): string[] {
  return ['basic-design', 'chunks', pjKey];
}

/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 展開済みチャンクの台帳を取得するフック（画面は展開済みチャンクを消せなくする判定に使う）
 * @graph-connects /basic-design/:pjKey/chunks [calls] チャンク台帳取得API
 */
export function useBasicDesignChunks(pjKey: string) {
  return useQuery({
    queryKey: basicDesignChunksKey(pjKey),
    queryFn: () => basicDesignExpansionApi.listChunks(pjKey),
    enabled: pjKey.length > 0,
  });
}

/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 基本設計シートをチャンクに分けて Backlog へ展開するフック。成功したら台帳と履歴を取り直す
 * @graph-connects /basic-design/:pjKey/expand-chunks [calls] チャンク分け展開API
 */
export function useExpandBasicDesignChunks(pjKey: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: ExpandChunksInput) =>
      basicDesignExpansionApi.expandChunks(pjKey, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: basicDesignChunksKey(pjKey) });
      void queryClient.invalidateQueries({ queryKey: basicDesignExpansionHistoryKey(pjKey) });
    },
  });
}
